import styled, { css, keyframes } from 'styled-components';

import { Text } from '../../../../components';
import { ANIM, COLORS, OWN_LIST, REDUCED_MOTION, SPACE } from '@/nova/theme/tokens';
import { LISTS_META_LINE_PX, LISTS_TITLE_LINE_PX, ListsHead } from '../GameLists/GameLists.styles';

/** The edit's moves (the user, 2026-10-20): the name and the field trading places, the row's search and actions
 *  stepping aside and back. */
const EDIT_MS = 240;
const SWAP_MS = 180;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const underlineIn = keyframes`
  from {
    transform: scaleX(0);
  }
  to {
    transform: scaleX(1);
  }
`;

const checkIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.4) rotate(-45deg);
  }
  to {
    opacity: 1;
    transform: none;
  }
`;

/** The edit glyph beside an own list's name, and how much wider the check that takes its place is. */
export const EDIT_GLYPH_PX = 16;
export const DONE_OVER_GLYPH_PX = LISTS_TITLE_LINE_PX - EDIT_GLYPH_PX;

/* The list's name, set as the column of lists sets the page's (see ListsTitle). */
export const TitleLine = styled.h3<{ $back?: boolean }>`
  margin: 0;
  min-width: 0;
  /* Back from an edit, the name fades in where the field was. */
  ${({ $back }) =>
    $back &&
    css`
      animation: ${fadeIn} ${SWAP_MS}ms ${ANIM.EASE};

      ${REDUCED_MOTION} {
        animation: none;
      }
    `}
  font-size: 18px;
  font-weight: 700;
  line-height: ${LISTS_TITLE_LINE_PX}px;
  color: ${COLORS.heading};
  overflow-wrap: anywhere;
`;

export const TitleText = styled(Text)`
  min-width: 0;
`;

/* One of the owner's own lists: its name is the control that edits it, the edit glyph beside it saying so. Dimmed while
   another write holds it (`$dim`), not while its own new name is being stored. */
export const RenameBtn = styled.button<{ $dim?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${SPACE[8]};
  max-width: 100%;
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: text;

  &[aria-disabled='true'] {
    cursor: default;
  }

  ${({ $dim }) => ($dim ? 'opacity: 0.6;' : '')}

  &:focus-visible {
    outline: 2px solid ${COLORS.accent};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

export const EditGlyph = styled.span`
  display: inline-flex;
  flex: none;
  color: ${COLORS.textMuted};

  ${RenameBtn}:hover:not([aria-disabled='true']) & {
    color: ${COLORS.heading};
  }
`;

/* The name being edited, in place: the same type and line as the name it replaces (so the count under it stays put),
   underlined while it is a field (outside its box, taking no room). */
export const NameField = styled.input`
  grid-area: 1 / 1;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  height: ${LISTS_TITLE_LINE_PX}px;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 0;
  animation: ${fadeIn} ${SWAP_MS}ms ${ANIM.EASE};
  background: transparent;
  color: ${COLORS.heading};
  font-family: inherit;
  font-size: 18px;
  font-weight: 700;
  line-height: ${LISTS_TITLE_LINE_PX}px;
  outline: none;
`;

/* What sizes the field to what is typed in it (the user, 2026-10-21): the same text, unseen, in the same cell, so the
   field grows to the right as the name does, never narrower than the name it started from (EditLine's min-width). */
export const FieldSizer = styled.span`
  display: inline-grid;
  flex: 1 1 auto;
  min-width: 0;

  &::after {
    content: attr(data-value);
    grid-area: 1 / 1;
    visibility: hidden;
    overflow: hidden;
    white-space: pre;
    font-size: 18px;
    font-weight: 700;
    line-height: ${LISTS_TITLE_LINE_PX}px;
  }
`;

/* The name being edited and, at its right, the button that keeps it (as Enter does): for whoever does not think of
   Enter, and on a phone. On the name's own line, so nothing under it moves; as wide as the name it started from, and
   wider as a longer one is typed, up to the room the row has. */
export const EditLine = styled.div`
  position: relative;
  width: fit-content;
  max-width: 100%;
  display: flex;
  align-items: center;
  gap: ${SPACE[8]};
  height: ${LISTS_TITLE_LINE_PX}px;

  /* The field's underline, drawn out from the name's start under it (outside its box, taking no room). */
  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: calc(${LISTS_TITLE_LINE_PX}px + ${SPACE[8]});
    bottom: -1px;
    height: 1px;
    background: ${COLORS.accent};
    transform-origin: left center;
    animation: ${underlineIn} ${EDIT_MS}ms ${ANIM.EASE};
  }

  ${REDUCED_MOTION} {
    &::after,
    & > * {
      animation: none;
    }
  }
`;

/* Keeps the name: a small round check, its tap target past its edges (a finger's, on a phone). */
export const DoneBtn = styled.button`
  position: relative;
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${LISTS_TITLE_LINE_PX}px;
  height: ${LISTS_TITLE_LINE_PX}px;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: ${COLORS.accent};
  color: ${COLORS.pageBg};
  cursor: pointer;
  animation: ${checkIn} ${EDIT_MS}ms ${ANIM.EASE} both;

  ${REDUCED_MOTION} {
    animation: none;
  }

  &::after {
    content: '';
    position: absolute;
    inset: -${SPACE[12]};
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.accent};
    outline-offset: 2px;
  }
`;

/* Under the name: how many games, then, for one of the owner's own lists, "User list". */
export const MetaLine = styled.span`
  display: flex;
  align-items: center;
  gap: ${SPACE[8]};
  height: ${LISTS_META_LINE_PX}px;
`;

/* "User list": the lilac the owner's own lists were marked with in the column. */
export const OwnTag = styled(Text)`
  display: inline-flex;
  align-items: center;
  height: ${LISTS_META_LINE_PX}px;
  padding: 0 ${SPACE[8]};
  border: 1px solid ${OWN_LIST.border};
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  color: ${OWN_LIST.mark};
`;

/* The heading's row: the name and its count, then the search and the actions at its right; under the name when the row
   is too narrow for them all. */
export const HeadRow = styled(ListsHead)`
  position: relative;
  flex-wrap: wrap;
  row-gap: ${SPACE[12]};
`;

/* The search and the actions, at the row's right, centred on the name and its count. While the name is edited they fade
   out and step out of the row (`$away`), so the name's field takes its whole width. */
export const Side = styled.div<{ $away?: boolean }>`
  flex: 1 1 auto;
  align-self: center;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  min-width: 0;
  /* Stepping aside for an edit: fading out as its parts slide a little to the right, then hidden; back, the other way.
     Its place (out of the row while away) is not what moves: the parts are. */
  transition:
    opacity ${EDIT_MS}ms ${ANIM.EASE},
    visibility 0s linear ${({ $away }) => ($away ? EDIT_MS : 0)}ms;

  & > * {
    transition: transform ${EDIT_MS}ms ${ANIM.EASE};
  }

  ${({ $away }) =>
    $away
      ? css`
          position: absolute;
          top: 50%;
          right: 0;
          transform: translateY(-50%);
          opacity: 0;
          visibility: hidden;
          pointer-events: none;

          & > * {
            transform: translateX(${SPACE[16]});
          }
        `
      : ''}

  ${REDUCED_MOTION} {
    transition: none;

    & > * {
      transition: none;
    }
  }
`;

/* Where the list's search is drawn: as wide as the row spares, between a usable and a comfortable width. */
export const SearchSlot = styled.div`
  flex: 0 1 320px;
  min-width: 200px;
  display: flex;

  & > * {
    flex: 1 1 auto;
    max-width: none;
  }
`;

/* The list's actions; they wrap within themselves before they crowd the search. */
export const Actions = styled.div`
  flex: none;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
`;

/* While one of the owner's lists is renamed: under the field, which language it edits (one chip per website language,
   EN the name itself), in place of the count. The list editor's Name step shows the same chips under its name. */
export const LangLine = styled.span`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${SPACE[4]};
  min-height: ${LISTS_META_LINE_PX}px;
  animation: ${fadeIn} ${SWAP_MS}ms ${ANIM.EASE} both;

  ${REDUCED_MOTION} {
    animation: none;
  }
`;

export const LangChip = styled.button<{ $on: boolean }>`
  display: inline-flex;
  align-items: center;
  height: ${LISTS_META_LINE_PX}px;
  padding: 0 ${SPACE[8]};
  border: 1px solid ${({ $on }) => ($on ? COLORS.accent : COLORS.hairline)};
  border-radius: 999px;
  background: ${({ $on }) => ($on ? COLORS.surfaceMuted : 'transparent')};
  color: ${({ $on }) => ($on ? COLORS.heading : COLORS.textMuted)};
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;

  &:hover {
    color: ${COLORS.heading};
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.accent};
    outline-offset: 1px;
  }
`;
